import React, { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore, BillingCycle } from 'src/store/subscriptionStore';
import {
  useSubscriptionPlansQuery,
  useTrialEligibilityQuery,
  useBillingProfileQuery,
  useSubscriptionStateQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as CancelAnytimeSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/trial/cancel-anytime.svg';
import { ReactComponent as ContactSupportTrialSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/trial/contact-support.svg';
import { ReactComponent as PaymentsSecureSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/trial/payments-secure.svg';
import { ReactComponent as RecommendedStarSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/trial/recommended.svg';
import { ReactComponent as CheckmarkSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/checkout/checkmark.svg';
import { ReactComponent as ManagePaymentMethodNextSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/manage-payment-method-next.svg';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './SubscriptionPlans.scss';

export const SubscriptionPlans: FC = () => {
  const navigate = useNavigate();
  const { billingCycle, setBillingCycle, billingInfo } = useSubscriptionStore();
  const [activeCycle, setActiveCycle] = useState<BillingCycle>(billingCycle);

  // Authoritative billing country
  const { data: serverBillingProfile } = useBillingProfileQuery();
  const currentCountry = serverBillingProfile?.country || billingInfo.country;
  const { data: subscriptionState } = useSubscriptionStateQuery();

  // Load plans & trial eligibility from server API with country context
  const {
    data: plansData,
    isLoading: isPlansLoading,
    isError: isPlansError,
    error: plansErrorObj,
    refetch: refetchPlans,
  } = useSubscriptionPlansQuery(currentCountry || undefined);

  const { data: eligibilityData } = useTrialEligibilityQuery();

  const basicPlan =
    plansData?.plans?.find(
      (p) =>
        p.tier_slug === 'basic' && p.interval === (activeCycle === 'yearly' ? 'year' : 'month'),
    ) || plansData?.plans?.find((p) => p.tier_slug === 'basic');

  const proPlan = plansData?.plans?.find(
    (p) => p.tier_slug === 'pro' && p.interval === (activeCycle === 'yearly' ? 'year' : 'month'),
  );

  const proMonthPlan = plansData?.plans?.find(
    (p) => p.tier_slug === 'pro' && p.interval === 'month',
  );

  const proYearPlan = plansData?.plans?.find((p) => p.tier_slug === 'pro' && p.interval === 'year');

  const formatSubDate = (isoString?: string | null) => {
    if (!isoString) return null;
    try {
      const d = new Date(isoString);
      return !isNaN(d.getTime())
        ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
    } catch {
      return null;
    }
  };

  const isCancelled =
    subscriptionState?.status === 'cancelled' || Boolean(subscriptionState?.cancel_at_period_end);

  const isActivePro =
    subscriptionState?.tier_slug === 'pro' &&
    (subscriptionState?.status === 'active' || subscriptionState?.status === 'trialing') &&
    !isCancelled;

  const isPastDue =
    subscriptionState?.tier_slug === 'pro' && subscriptionState?.status === 'past_due';

  const isProCurrent =
    (subscriptionState?.tier_slug === 'pro' &&
      ['active', 'trialing', 'cancelled', 'past_due'].includes(subscriptionState.status)) ||
    Boolean(plansData?.plans?.some((plan) => plan.tier_slug === 'pro' && plan.is_current));

  const accessUntilFormatted = formatSubDate(
    subscriptionState?.access_until || subscriptionState?.current_period_end,
  );

  const basicFeatures = basicPlan?.features ?? [];
  const proFeatures = proPlan?.features ?? [];

  // Server-verified trial eligibility (Backend is authoritative)
  const serverTrialEligible = eligibilityData?.trial_eligible ?? plansData?.trial_eligible ?? false;
  const canStartTrial = serverTrialEligible && !isProCurrent && !isCancelled;

  const handleCycleChange = (cycle: BillingCycle) => {
    setActiveCycle(cycle);
    setBillingCycle(cycle);
  };

  const handleSelectPro = () => {
    setBillingCycle(activeCycle);
    navigate(`/onboarding-trainer/dashboard/subscription/checkout?cycle=${activeCycle}`);
  };

  const handleGoToMySubscription = () => {
    navigate('/onboarding-trainer/dashboard/subscription/my-subscription');
  };

  const handleGoToReactivate = () => {
    navigate('/onboarding-trainer/dashboard/subscription/reactivate');
  };

  const handleGoToCancel = () => {
    navigate('/onboarding-trainer/dashboard/subscription/cancel');
  };

  const handleGoToSupport = () => {
    navigate('/onboarding-trainer/dashboard/subscription/contact-support');
  };

  if (isPlansLoading) {
    return (
      <div
        className="subscription-plans-page"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#E2F163' }} spin />} />
      </div>
    );
  }

  if (isPlansError) {
    const errorDetails = plansErrorObj as any;
    const userMessage =
      errorDetails?.user_message ||
      errorDetails?.message ||
      'Unable to load subscription plans. Subscriptions may not be supported in your country yet.';

    return (
      <div className="subscription-plans-page">
        <div className="plans-page-header">
          <div className="header-titles">
            <h1>{'Subscription Plans'}</h1>
            <p>{'Choose the plan that fits how you coach and build programs.'}</p>
          </div>
        </div>
        <div
          style={{
            margin: '40px auto',
            maxWidth: 600,
            padding: 32,
            background: 'rgba(255, 255, 255, 0.04)',
            borderRadius: 16,
            textAlign: 'center',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h3 style={{ color: '#FF7D7D', marginBottom: 12 }}>{'Unable to Load Plans'}</h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: 24 }}>{userMessage}</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              type="button"
              className="btn-upgrade-pro"
              style={{ width: 'auto', padding: '10px 24px' }}
              onClick={() => refetchPlans()}
            >
              {'Retry'}
            </button>
            <button
              type="button"
              className="btn-current-plan"
              style={{ width: 'auto', padding: '10px 24px', cursor: 'pointer' }}
              onClick={handleGoToSupport}
            >
              {'Contact Support'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only display "My Subscription Plan" if the user has or ever had a subscription
  const hasHadSubscription =
    Boolean(subscriptionState?.has_had_subscription) ||
    (subscriptionState?.status && subscriptionState.status !== 'inactive') ||
    Boolean(subscriptionState?.has_used_trial) ||
    Boolean(subscriptionState?.current_period_start) ||
    isProCurrent;

  return (
    <div className="subscription-plans-page">
      {/* Header */}
      <div className="plans-page-header">
        <div className="header-titles">
          <h1>{'Subscription Plans'}</h1>
          <p>{'Choose the plan that fits how you coach and build programs.'}</p>
        </div>
        {hasHadSubscription && (
          <button type="button" className="my-plan-nav-btn" onClick={handleGoToMySubscription}>
            <span>{'My Subscription Plan'}</span>
            <ManagePaymentMethodNextSVG />
          </button>
        )}
      </div>

      {/* Cycle Switcher (Monthly / Yearly) */}
      <div className="billing-cycle-toggle-wrapper">
        <div className="billing-cycle-toggle">
          <button
            type="button"
            className={`cycle-btn ${activeCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => handleCycleChange('monthly')}
          >
            {'Monthly'}
          </button>
          <button
            type="button"
            className={`cycle-btn ${activeCycle === 'yearly' ? 'active' : ''}`}
            onClick={() => handleCycleChange('yearly')}
          >
            <span>{'Yearly'}</span>
            {proYearPlan?.savings_percent ? (
              <span className="save-badge">{`SAVE ${proYearPlan.savings_percent}%`}</span>
            ) : null}
          </button>
        </div>
      </div>

      {/* Plan Cards Grid */}
      <div className="plans-grid">
        {/* Basic Plan Card */}
        {basicPlan && (
          <div className="plan-card basic-card">
            <div className="plan-card-header">
              <div className="plan-title-row">
                <h3>{basicPlan.name}</h3>
                {!isProCurrent && !isCancelled && (
                  <span className="current-plan-badge">{'Current plan'}</span>
                )}
              </div>
              <div className="plan-price-row">
                <span className="price-amount">{basicPlan.formatted_amount}</span>
                <span className="price-period">{basicPlan.billing_period}</span>
              </div>
              {basicPlan.description && <p className="plan-subtitle">{basicPlan.description}</p>}
            </div>

            <div className="plan-features-list">
              {basicFeatures.map((feat) => (
                <div className="feature-item" key={feat}>
                  <CheckmarkSVG className="feature-check white-check" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            <div className="plan-card-footer">
              {isCancelled ? (
                <>
                  <button type="button" className="btn-current-plan" disabled>
                    {'Downgrade Scheduled'}
                  </button>
                  {accessUntilFormatted && (
                    <p className="plan-status-subtext">{`Active from ${accessUntilFormatted}`}</p>
                  )}
                </>
              ) : isActivePro ? (
                <button type="button" className="btn-downgrade-to-basic" onClick={handleGoToCancel}>
                  {'Downgrade to Basic'}
                </button>
              ) : (
                <button type="button" className="btn-current-plan" disabled>
                  {'Current Plan'}
                </button>
              )}
              {canStartTrial && <div className="trial-disclaimer-spacer" />}
            </div>
          </div>
        )}

        {/* Pro Plan Card */}
        {proPlan && (
          <div className="plan-card pro-card">
            <div className="plan-card-header">
              <div className="plan-title-row">
                <h3>{proPlan.name}</h3>
                {isCancelled ? (
                  <span className="cancelled-plan-badge">{'Cancelled'}</span>
                ) : proPlan.recommended || proPlan.tier_slug === 'pro' ? (
                  <span className="recommended-badge">
                    <RecommendedStarSVG />
                    <span>{'Recommended'}</span>
                  </span>
                ) : null}
              </div>
              <div className="plan-price-row">
                <span className="price-amount">{proPlan.formatted_amount}</span>
                <span className="price-period">{proPlan.billing_period}</span>
              </div>
              {activeCycle === 'monthly' &&
                (canStartTrial ? (
                  <p className="plan-subtitle">
                    {`${plansData?.currency_symbol || '£'}0 for the first two weeks. ${proMonthPlan?.formatted_amount || proPlan.formatted_amount}/month after trial`}
                  </p>
                ) : proPlan.description ? (
                  <p className="plan-subtitle">{proPlan.description}</p>
                ) : null)}
              {proPlan.tagline && <p className="plan-tagline">{proPlan.tagline}</p>}
            </div>

            <div className="plan-features-list">
              {proFeatures.map((feat) => (
                <div className="feature-item" key={feat}>
                  <CheckmarkSVG className="feature-check gold-check" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            <div className="plan-card-footer">
              {isCancelled ? (
                <>
                  <button
                    type="button"
                    className="btn-upgrade-pro btn-reactivate-plans-cta"
                    onClick={handleGoToReactivate}
                  >
                    {'Reactivate Pro'}
                  </button>
                  {accessUntilFormatted && (
                    <p className="trial-disclaimer">
                      {`Pro access active until ${accessUntilFormatted}`}
                    </p>
                  )}
                </>
              ) : isPastDue ? (
                <>
                  <button
                    type="button"
                    className="btn-upgrade-pro"
                    onClick={handleGoToMySubscription}
                  >
                    {'Resolve Payment'}
                  </button>
                  <p className="trial-disclaimer error-text">
                    {'Payment is past due. Please update payment method.'}
                  </p>
                </>
              ) : isActivePro ? (
                <button type="button" className="btn-current-plan" disabled>
                  {'Current Plan'}
                </button>
              ) : (
                <>
                  <button type="button" className="btn-upgrade-pro" onClick={handleSelectPro}>
                    {canStartTrial && plansData?.trial_days
                      ? `Start ${plansData.trial_days}-day free trial`
                      : 'Upgrade to Pro'}
                  </button>
                  {canStartTrial && proPlan && (
                    <p className="trial-disclaimer">
                      {`No charge today. ${proPlan.formatted_amount}${proPlan.billing_period} after trial. Cancel anytime before your first payment.`}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom Features / Trust Banner */}
      <div className="trust-cards-grid">
        <div className="trust-card">
          <div className="trust-icon">
            <CancelAnytimeSVG />
          </div>
          <div className="trust-text">
            <h4>{'Cancel anytime'}</h4>
            <p>{'No lock-in contracts. Cancel or change plans at any time.'}</p>
          </div>
        </div>

        <div className="trust-card clickable" onClick={handleGoToSupport}>
          <div className="trust-icon">
            <ContactSupportTrialSVG />
          </div>
          <div className="trust-text">
            <h4>{'Questions? Contact support'}</h4>
            <p>{"We're here to help you."}</p>
          </div>
        </div>

        <div className="trust-card">
          <div className="trust-icon">
            <PaymentsSecureSVG />
          </div>
          <div className="trust-text">
            <h4>{'Payments secure and encrypted'}</h4>
            <p>{'All plans include secure data'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
